// oauth-provider.js — shared OAuth plumbing for the bearer-token calendar
// back-ends (Google Calendar v3, Microsoft Graph).
//
// Both providers hydrate their credentials from the workspace vault and
// funnel every outbound request through the token-manager, differing only in
// their REST surface (endpoints, event normalisation). This base owns the
// identical parts so a fix to the token/vault contract lands once:
//
//   _loadToken()   — hydrate { access_token, refresh_token, expires_at } from
//                    the workspace vault (cached on `this._token`).
//   _requestOpts() — proactive getValidAccessToken refresh + the reactive
//                    `refresh` callback transport.js invokes on a 401.
//   init()         — super.init() then _loadToken().
//
// The `provider` string handed to the token-manager is `this.id` ('google' /
// 'microsoft'), set by each subclass's constructor.

import { CalendarProvider, CalendarProviderError, ERROR_CODES } from './calendar-provider.js';

export class OAuthCalendarProvider extends CalendarProvider {
  /** @returns {Promise<any>} the stored token set. */
  async _loadToken() {
    if (this._token) return this._token;
    // `|| null` drops the `false` arm of the `typeof window && window.X` guard so
    // vault is WorkspaceSessionVault | null and the ?. below narrows cleanly.
    const vault = (typeof window !== 'undefined' && window.WorkspaceSessionVault) || null;
    if (!vault?.load)
      throw new CalendarProviderError(ERROR_CODES.AUTH_REQUIRED, 'vault unavailable');
    const raw = await vault.load(this.credentialKey);
    if (!raw) throw new CalendarProviderError(ERROR_CODES.AUTH_REQUIRED, 'no credentials');
    this._token = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return this._token;
  }

  // Build the Transport request options for the active token. Proactively
  // refreshes through token-manager.getValidAccessToken BEFORE the request
  // goes out: if the stored token is within the manager's skew window it is
  // refreshed via the single-flight `refresh()` and `this._token` is updated
  // with the fresh set. The `refresh` callback below stays as the reactive
  // fallback transport.js invokes on a 401 (revocation / server-side early
  // expiry beyond the skew window).
  /** @returns {Promise<any>} transport request options for the active token. */
  async _requestOpts() {
    // `|| null` drops the `false` arm so mgr is CalendarTokenManager | null and
    // the ?. below narrows cleanly (same falsy runtime behaviour).
    const mgr = (typeof window !== 'undefined' && window.CalendarTokenManager) || null;
    if (mgr?.getValidAccessToken) {
      const valid = await mgr.getValidAccessToken({
        provider: this.id,
        accountId: this.accountId,
        credentialKey: this.credentialKey,
      });
      if (valid) this._token = valid;
    }
    const refresh = async (accountId: any) => {
      // Hand off to the token-manager (Phase 4 module). If it isn't
      // loaded, fail loudly so the caller surfaces AUTH_REQUIRED rather
      // than retrying with a stale token.
      const m = (typeof window !== 'undefined' && window.CalendarTokenManager) || null;
      if (!m?.refresh) {
        throw new CalendarProviderError(ERROR_CODES.AUTH_REQUIRED, 'token manager unavailable');
      }
      const next = await m.refresh({
        provider: this.id,
        accountId,
        credentialKey: this.credentialKey,
      });
      this._token = next;
      return { token: next?.access_token };
    };
    return {
      accountId: this.accountId,
      credentials: { kind: 'bearer', token: this._token?.access_token },
      refresh,
    };
  }

  /** @returns {Promise<void>} */
  async init() {
    await super.init();
    await this._loadToken();
  }
}
